import React, { useState, useEffect } from 'react';
import { FaSpinner, FaComments, FaEnvelope, FaCalendarAlt, FaFileAlt, FaCog, FaCar, FaExclamationCircle, FaBug } from 'react-icons/fa';
import FrontendNotificationsAPI from '../api/frontendNotifications';
import '../css/NotificationManagement.css';

const NotificationManagement = () => {
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await FrontendNotificationsAPI.getPreferences();
      
      setPreferences(data);
    } catch (err) {
      console.error('❌ Error loading preferences:', err);
      setError('Hiba történt a beállítások betöltésekor: ' + err.message);
      
      const defaults = getDefaultPreferences();
      setPreferences(defaults);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPreferences = () => ({
    // Chat
    chat_message_daily_email: false,
    chat_message_instant_email: false,
    chat_message_system: true,
    chat_message_push: true,

    // Email
    new_email_daily_email: false,
    new_email_instant_email: false,
    new_email_system: true,
    new_email_push: true,

    // Naptár
    calendar_event_created_daily_email: false,
    calendar_event_created_instant_email: false,
    calendar_event_created_system: true,
    calendar_event_created_push: true,
    calendar_event_updated_daily_email: false,
    calendar_event_updated_instant_email: false,
    calendar_event_updated_system: true,
    calendar_event_updated_push: true,
    calendar_event_deleted_daily_email: false,
    calendar_event_deleted_instant_email: false,
    calendar_event_deleted_system: true,
    calendar_event_deleted_push: true,
    calendar_booking_changed_daily_email: false,
    calendar_booking_changed_instant_email: false,
    calendar_booking_changed_system: true,
    calendar_booking_changed_push: true,

    // Dokumentumok
    document_uploaded_daily_email: false,
    document_uploaded_instant_email: false,
    document_uploaded_system: true,
    document_uploaded_push: true,
    document_shared_daily_email: false,
    document_shared_instant_email: false,
    document_shared_system: true,
    document_shared_push: true,

    // Rendszer
    system_maintenance_daily_email: false,
    system_maintenance_instant_email: false,
    system_maintenance_system: true,
    system_maintenance_push: true,

    // Járműkezelés
    vehicle_added_to_company_daily_email: false,
    vehicle_added_to_company_instant_email: false,
    vehicle_added_to_company_system: true,
    vehicle_added_to_company_push: true,
    vehicle_assigned_to_user_daily_email: false,
    vehicle_assigned_to_user_instant_email: true,
    vehicle_assigned_to_user_system: true,
    vehicle_assigned_to_user_push: true,
    vehicle_unassigned_from_user_daily_email: false,
    vehicle_unassigned_from_user_instant_email: false,
    vehicle_unassigned_from_user_system: true,
    vehicle_unassigned_from_user_push: true,
    vehicle_document_expiring_daily_email: false,
    vehicle_document_expiring_instant_email: true,
    vehicle_document_expiring_system: true,
    vehicle_document_expiring_push: true,
    vehicle_service_due_daily_email: false,
    vehicle_service_due_instant_email: true,
    vehicle_service_due_system: true,
    vehicle_service_due_push: true,
    vehicle_rental_starting_soon_daily_email: false,
    vehicle_rental_starting_soon_instant_email: true,
    vehicle_rental_starting_soon_system: true,
    vehicle_rental_starting_soon_push: true,
    vehicle_rental_ending_soon_daily_email: false,
    vehicle_rental_ending_soon_instant_email: true,
    vehicle_rental_ending_soon_system: true,
    vehicle_rental_ending_soon_push: true,

    // Reklamáció
    complaint_updated_daily_email: false,
    complaint_updated_instant_email: true,
    complaint_updated_system: true,
    complaint_updated_push: true,
    complaint_response_daily_email: false,
    complaint_response_instant_email: true,
    complaint_response_system: true,
    complaint_response_push: true,
    complaint_resolved_daily_email: false,
    complaint_resolved_instant_email: true,
    complaint_resolved_system: true,
    complaint_resolved_push: true,

    // Hibabejelentés
    ticket_response_daily_email: false,
    ticket_response_instant_email: true,
    ticket_response_system: true,
    ticket_response_push: true,
    ticket_resolved_daily_email: false,
    ticket_resolved_instant_email: true,
    ticket_resolved_system: true,
    ticket_resolved_push: true
  });

    const handleChange = (key) => {
    setPreferences(prev => {
      const newPrefs = {
        ...prev,
        [key]: !prev[key]
      };
      return newPrefs;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();


    try {
      setSaving(true);
      setError(null);
      setSuccess('');

      const updatedData = await FrontendNotificationsAPI.updatePreferences(preferences);

      // Set preferences from the response data instead of reloading
      if (updatedData && updatedData.data) {
        setPreferences(updatedData.data);
      }

      setSuccess('✅ Beállítások sikeresen elmentve!');

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('❌ Error updating preferences:', err);

      if (err.message.includes('client_user_id')) {
        setError('Azonosítási hiba: Jelentkezz ki és be újra, majd próbáld meg ismét.');
      } else if (err.message.includes('Failed to update preferences')) {
        setError('A backend nem tudta menteni a beállításokat. Ellenőrizd, hogy be vagy-e jelentkezve.');
      } else {
        setError('Hiba történt a beállítások mentésekor: ' + err.message);
      }
    } finally {
      setSaving(false);
    }
  };



  if (loading) {
    return (
      <div className="notif-mgmt-loading">
        <FaSpinner className="fa-spin" />
        <span>Beállítások betöltése...</span>
      </div>
    );
  }

  return (
    <div className="notif-mgmt-container">
      <div className="notif-mgmt-header">
        <h2>Értesítési beállítások</h2>
      </div>

      {error && (
        <div className="notif-mgmt-error">
          {error}
          <button onClick={loadPreferences} className="notif-mgmt-retry-btn">
            Újrapróbálás
          </button>
        </div>
      )}

      {success && (
        <div className="notif-mgmt-success">
          {success}
        </div>
      )}

    <form className="notif-mgmt-form" onSubmit={handleSubmit}>
        
        
        <section className="notif-mgmt-section">
          <h3><FaComments /> Chat üzenetek</h3>
          <div className="notif-mgmt-table">
            <div className="notif-mgmt-row notif-mgmt-header">
              <div className="notif-mgmt-cell notif-mgmt-func">Esemény</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Napi email</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Azonnali email</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Rendszerben</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Push értesítés</div>
            </div>
            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Chat üzenet érkezik</strong>
                <div className="notif-mgmt-desc">Értesítést kérek, ha chat üzenetet kapok.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input
                  type="checkbox"
                  checked={preferences.chat_message_daily_email || false}
                  onChange={() => handleChange('chat_message_daily_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input
                  type="checkbox"
                  checked={preferences.chat_message_instant_email || false}
                  onChange={() => handleChange('chat_message_instant_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input
                  type="checkbox"
                  checked={preferences.chat_message_system || false}
                  onChange={() => handleChange('chat_message_system')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.chat_message_push || false}
                  onChange={() => handleChange('chat_message_push')}
                />
              </div>
            </div>
          </div>
        </section>

        
        <section className="notif-mgmt-section">
          <h3><FaEnvelope /> Email üzenetek</h3>
          <div className="notif-mgmt-table">
            <div className="notif-mgmt-row notif-mgmt-header">
              <div className="notif-mgmt-cell notif-mgmt-func">Esemény</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Napi email</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Azonnali email</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Rendszerben</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Push értesítés</div>
            </div>
            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Új email érkezett</strong>
                <div className="notif-mgmt-desc">Értesítést kérek, ha új email érkezett a rendszerbe.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input
                  type="checkbox"
                  checked={preferences.new_email_daily_email || false}
                  onChange={() => handleChange('new_email_daily_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input
                  type="checkbox"
                  checked={preferences.new_email_instant_email || false}
                  onChange={() => handleChange('new_email_instant_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input
                  type="checkbox"
                  checked={preferences.new_email_system || false}
                  onChange={() => handleChange('new_email_system')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.new_email_push || false}
                  onChange={() => handleChange('new_email_push')}
                />
              </div>
            </div>
          </div>
        </section>

       
        <section className="notif-mgmt-section">
          <h3><FaCalendarAlt /> Naptár események</h3>
          <div className="notif-mgmt-table">
            <div className="notif-mgmt-row notif-mgmt-header">
              <div className="notif-mgmt-cell notif-mgmt-func">Esemény</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Napi email</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Azonnali email</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Rendszerben</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Push értesítés</div>
            </div>
            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Esemény létrehozva</strong>
                <div className="notif-mgmt-desc">Értesítést kérek, ha új eseményt hoztak létre.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input
                  type="checkbox"
                  checked={preferences.calendar_event_created_daily_email || false}
                  onChange={() => handleChange('calendar_event_created_daily_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input
                  type="checkbox"
                  checked={preferences.calendar_event_created_instant_email || false}
                  onChange={() => handleChange('calendar_event_created_instant_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input
                  type="checkbox"
                  checked={preferences.calendar_event_created_system || false}
                  onChange={() => handleChange('calendar_event_created_system')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.calendar_event_created_push || false}
                  onChange={() => handleChange('calendar_event_created_push')}
                />
              </div>
            </div>
            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Esemény módosítva</strong>
                <div className="notif-mgmt-desc">Értesítést kérek, ha egy eseményt módosítottak.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input
                  type="checkbox"
                  checked={preferences.calendar_event_updated_daily_email || false}
                  onChange={() => handleChange('calendar_event_updated_daily_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input
                  type="checkbox"
                  checked={preferences.calendar_event_updated_instant_email || false}
                  onChange={() => handleChange('calendar_event_updated_instant_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input
                  type="checkbox"
                  checked={preferences.calendar_event_updated_system || false}
                  onChange={() => handleChange('calendar_event_updated_system')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.calendar_event_updated_push || false}
                  onChange={() => handleChange('calendar_event_updated_push')}
                />
              </div>
            </div>
            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Foglalás változott</strong>
                <div className="notif-mgmt-desc">Értesítést kérek, ha egy naptári foglalás megváltozott.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input
                  type="checkbox"
                  checked={preferences.calendar_booking_changed_daily_email || false}
                  onChange={() => handleChange('calendar_booking_changed_daily_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input
                  type="checkbox"
                  checked={preferences.calendar_booking_changed_instant_email || false}
                  onChange={() => handleChange('calendar_booking_changed_instant_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input
                  type="checkbox"
                  checked={preferences.calendar_booking_changed_system || false}
                  onChange={() => handleChange('calendar_booking_changed_system')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.calendar_booking_changed_push || false}
                  onChange={() => handleChange('calendar_booking_changed_push')}
                />
              </div>
            </div>
            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Esemény törölve</strong>
                <div className="notif-mgmt-desc">Értesítést kérek, ha egy eseményt töröltek.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input
                  type="checkbox"
                  checked={preferences.calendar_event_deleted_daily_email || false}
                  onChange={() => handleChange('calendar_event_deleted_daily_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input
                  type="checkbox"
                  checked={preferences.calendar_event_deleted_instant_email || false}
                  onChange={() => handleChange('calendar_event_deleted_instant_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input
                  type="checkbox"
                  checked={preferences.calendar_event_deleted_system || false}
                  onChange={() => handleChange('calendar_event_deleted_system')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.calendar_event_deleted_push || false}
                  onChange={() => handleChange('calendar_event_deleted_push')}
                />
              </div>
            </div>
          </div>
        </section>

        
        <section className="notif-mgmt-section">
          <h3><FaFileAlt /> Dokumentumok</h3>
          <div className="notif-mgmt-table">
            <div className="notif-mgmt-row notif-mgmt-header">
              <div className="notif-mgmt-cell notif-mgmt-func">Esemény</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Napi email</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Azonnali email</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Rendszerben</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Push értesítés</div>
            </div>
            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Dokumentum feltöltve</strong>
                <div className="notif-mgmt-desc">Értesítést kérek, ha új dokumentumot töltöttek fel.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input
                  type="checkbox"
                  checked={preferences.document_uploaded_daily_email || false}
                  onChange={() => handleChange('document_uploaded_daily_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input
                  type="checkbox"
                  checked={preferences.document_uploaded_instant_email || false}
                  onChange={() => handleChange('document_uploaded_instant_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input
                  type="checkbox"
                  checked={preferences.document_uploaded_system || false}
                  onChange={() => handleChange('document_uploaded_system')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.document_uploaded_push || false}
                  onChange={() => handleChange('document_uploaded_push')}
                />
              </div>
            </div>
            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Dokumentum megosztva</strong>
                <div className="notif-mgmt-desc">Értesítést kérek, ha velem megosztottak egy dokumentumot.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input
                  type="checkbox"
                  checked={preferences.document_shared_daily_email || false}
                  onChange={() => handleChange('document_shared_daily_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input
                  type="checkbox"
                  checked={preferences.document_shared_instant_email || false}
                  onChange={() => handleChange('document_shared_instant_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input
                  type="checkbox"
                  checked={preferences.document_shared_system || false}
                  onChange={() => handleChange('document_shared_system')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.document_shared_push || false}
                  onChange={() => handleChange('document_shared_push')}
                />
              </div>
            </div>
          </div>
        </section>



       
        <section className="notif-mgmt-section">
          <h3><FaCog /> Rendszer</h3>
          <div className="notif-mgmt-table">
            <div className="notif-mgmt-row notif-mgmt-header">
              <div className="notif-mgmt-cell notif-mgmt-func">Esemény</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Napi email</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Azonnali email</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Rendszerben</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Push értesítés</div>
            </div>
            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Rendszer karbantartás</strong>
                <div className="notif-mgmt-desc">Értesítést kérek a rendszer karbantartásokról.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input
                  type="checkbox"
                  checked={preferences.system_maintenance_daily_email || false}
                  onChange={() => handleChange('system_maintenance_daily_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input
                  type="checkbox"
                  checked={preferences.system_maintenance_instant_email || false}
                  onChange={() => handleChange('system_maintenance_instant_email')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input
                  type="checkbox"
                  checked={preferences.system_maintenance_system || false}
                  onChange={() => handleChange('system_maintenance_system')}
                />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.system_maintenance_push || false}
                  onChange={() => handleChange('system_maintenance_push')}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Járműkezelés értesítések */}
        <section className="notif-mgmt-section">
          <h3><FaCar /> Járműkezelés</h3>
          <div className="notif-mgmt-table">
            <div className="notif-mgmt-row notif-mgmt-header">
              <div className="notif-mgmt-cell notif-mgmt-func">Esemény</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Napi email</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Azonnali email</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Rendszerben</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Push értesítés</div>
            </div>

            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Jármű hozzáadva céghez</strong>
                <div className="notif-mgmt-desc">Új jármű került a céghez.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input type="checkbox" checked={preferences.vehicle_added_to_company_daily_email || false}
                  onChange={() => handleChange('vehicle_added_to_company_daily_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input type="checkbox" checked={preferences.vehicle_added_to_company_instant_email || false}
                  onChange={() => handleChange('vehicle_added_to_company_instant_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input type="checkbox" checked={preferences.vehicle_added_to_company_system || false}
                  onChange={() => handleChange('vehicle_added_to_company_system')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.vehicle_added_to_company_push || false}
                  onChange={() => handleChange('vehicle_added_to_company_push')}
                />
              </div>
            </div>

            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Jármű hozzárendelve Önhöz</strong>
                <div className="notif-mgmt-desc">Jármű került hozzárendelésre az Ön részére.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input type="checkbox" checked={preferences.vehicle_assigned_to_user_daily_email || false}
                  onChange={() => handleChange('vehicle_assigned_to_user_daily_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input type="checkbox" checked={preferences.vehicle_assigned_to_user_instant_email || false}
                  onChange={() => handleChange('vehicle_assigned_to_user_instant_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input type="checkbox" checked={preferences.vehicle_assigned_to_user_system || false}
                  onChange={() => handleChange('vehicle_assigned_to_user_system')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.vehicle_assigned_to_user_push || false}
                  onChange={() => handleChange('vehicle_assigned_to_user_push')}
                />
              </div>
            </div>

            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Jármű elvéve Öntől</strong>
                <div className="notif-mgmt-desc">Jármű el lett véve Öntől.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input type="checkbox" checked={preferences.vehicle_unassigned_from_user_daily_email || false}
                  onChange={() => handleChange('vehicle_unassigned_from_user_daily_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input type="checkbox" checked={preferences.vehicle_unassigned_from_user_instant_email || false}
                  onChange={() => handleChange('vehicle_unassigned_from_user_instant_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input type="checkbox" checked={preferences.vehicle_unassigned_from_user_system || false}
                  onChange={() => handleChange('vehicle_unassigned_from_user_system')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.vehicle_unassigned_from_user_push || false}
                  onChange={() => handleChange('vehicle_unassigned_from_user_push')}
                />
              </div>
            </div>

            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Jármű dokumentum hamarosan lejár</strong>
                <div className="notif-mgmt-desc">Jármű dokumentum lejárati ideje közeleg.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input type="checkbox" checked={preferences.vehicle_document_expiring_daily_email || false}
                  onChange={() => handleChange('vehicle_document_expiring_daily_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input type="checkbox" checked={preferences.vehicle_document_expiring_instant_email || false}
                  onChange={() => handleChange('vehicle_document_expiring_instant_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input type="checkbox" checked={preferences.vehicle_document_expiring_system || false}
                  onChange={() => handleChange('vehicle_document_expiring_system')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.vehicle_document_expiring_push || false}
                  onChange={() => handleChange('vehicle_document_expiring_push')}
                />
              </div>
            </div>

            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Jármű szerviz esedékes</strong>
                <div className="notif-mgmt-desc">Jármű szerviz ideje közeleg.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input type="checkbox" checked={preferences.vehicle_service_due_daily_email || false}
                  onChange={() => handleChange('vehicle_service_due_daily_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input type="checkbox" checked={preferences.vehicle_service_due_instant_email || false}
                  onChange={() => handleChange('vehicle_service_due_instant_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input type="checkbox" checked={preferences.vehicle_service_due_system || false}
                  onChange={() => handleChange('vehicle_service_due_system')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.vehicle_service_due_push || false}
                  onChange={() => handleChange('vehicle_service_due_push')}
                />
              </div>
            </div>

            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Bérlés hamarosan kezdődik</strong>
                <div className="notif-mgmt-desc">Jármű bérlés kezdési ideje közeleg.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input type="checkbox" checked={preferences.vehicle_rental_starting_soon_daily_email || false}
                  onChange={() => handleChange('vehicle_rental_starting_soon_daily_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input type="checkbox" checked={preferences.vehicle_rental_starting_soon_instant_email || false}
                  onChange={() => handleChange('vehicle_rental_starting_soon_instant_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input type="checkbox" checked={preferences.vehicle_rental_starting_soon_system || false}
                  onChange={() => handleChange('vehicle_rental_starting_soon_system')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.vehicle_rental_starting_soon_push || false}
                  onChange={() => handleChange('vehicle_rental_starting_soon_push')}
                />
              </div>
            </div>

            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Bérlés hamarosan véget ér</strong>
                <div className="notif-mgmt-desc">Jármű bérlés befejezési ideje közeleg.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input type="checkbox" checked={preferences.vehicle_rental_ending_soon_daily_email || false}
                  onChange={() => handleChange('vehicle_rental_ending_soon_daily_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input type="checkbox" checked={preferences.vehicle_rental_ending_soon_instant_email || false}
                  onChange={() => handleChange('vehicle_rental_ending_soon_instant_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input type="checkbox" checked={preferences.vehicle_rental_ending_soon_system || false}
                  onChange={() => handleChange('vehicle_rental_ending_soon_system')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.vehicle_rental_ending_soon_push || false}
                  onChange={() => handleChange('vehicle_rental_ending_soon_push')}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Reklamáció értesítések */}
        <section className="notif-mgmt-section">
          <h3><FaExclamationCircle /> Reklamáció</h3>
          <div className="notif-mgmt-table">
            <div className="notif-mgmt-row notif-mgmt-header">
              <div className="notif-mgmt-cell notif-mgmt-func">Esemény</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Napi email</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Azonnali email</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Rendszerben</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Push értesítés</div>
            </div>

            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Reklamáció frissítve</strong>
                <div className="notif-mgmt-desc">Reklamáció státusza megváltozott.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input type="checkbox" checked={preferences.complaint_updated_daily_email || false}
                  onChange={() => handleChange('complaint_updated_daily_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input type="checkbox" checked={preferences.complaint_updated_instant_email || false}
                  onChange={() => handleChange('complaint_updated_instant_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input type="checkbox" checked={preferences.complaint_updated_system || false}
                  onChange={() => handleChange('complaint_updated_system')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.complaint_updated_push || false}
                  onChange={() => handleChange('complaint_updated_push')}
                />
              </div>
            </div>

            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Válasz érkezett reklamációra</strong>
                <div className="notif-mgmt-desc">Válasz érkezett az Ön reklamációjára.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input type="checkbox" checked={preferences.complaint_response_daily_email || false}
                  onChange={() => handleChange('complaint_response_daily_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input type="checkbox" checked={preferences.complaint_response_instant_email || false}
                  onChange={() => handleChange('complaint_response_instant_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input type="checkbox" checked={preferences.complaint_response_system || false}
                  onChange={() => handleChange('complaint_response_system')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.complaint_response_push || false}
                  onChange={() => handleChange('complaint_response_push')}
                />
              </div>
            </div>

            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Reklamáció megoldva</strong>
                <div className="notif-mgmt-desc">Az Ön reklamációja megoldásra került.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input type="checkbox" checked={preferences.complaint_resolved_daily_email || false}
                  onChange={() => handleChange('complaint_resolved_daily_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input type="checkbox" checked={preferences.complaint_resolved_instant_email || false}
                  onChange={() => handleChange('complaint_resolved_instant_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input type="checkbox" checked={preferences.complaint_resolved_system || false}
                  onChange={() => handleChange('complaint_resolved_system')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.complaint_resolved_push || false}
                  onChange={() => handleChange('complaint_resolved_push')}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Ticket/Hibabejelentés értesítések */}
        <section className="notif-mgmt-section">
          <h3><FaBug /> Hibabejelentés</h3>
          <div className="notif-mgmt-table">
            <div className="notif-mgmt-row notif-mgmt-header">
              <div className="notif-mgmt-cell notif-mgmt-func">Esemény</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Napi email</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Azonnali email</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Rendszerben</div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox">Push értesítés</div>
            </div>

            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Válasz érkezett hibabejelentésre</strong>
                <div className="notif-mgmt-desc">Válasz érkezett az Ön hibabejelentésére.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input type="checkbox" checked={preferences.ticket_response_daily_email || false}
                  onChange={() => handleChange('ticket_response_daily_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input type="checkbox" checked={preferences.ticket_response_instant_email || false}
                  onChange={() => handleChange('ticket_response_instant_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input type="checkbox" checked={preferences.ticket_response_system || false}
                  onChange={() => handleChange('ticket_response_system')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.ticket_response_push || false}
                  onChange={() => handleChange('ticket_response_push')}
                />
              </div>
            </div>

            <div className="notif-mgmt-row">
              <div className="notif-mgmt-cell notif-mgmt-func">
                <strong>Hibabejelentés megoldva</strong>
                <div className="notif-mgmt-desc">Az Ön hibabejelentése megoldásra került.</div>
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Napi email">
                <input type="checkbox" checked={preferences.ticket_resolved_daily_email || false}
                  onChange={() => handleChange('ticket_resolved_daily_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Azonnali email">
                <input type="checkbox" checked={preferences.ticket_resolved_instant_email || false}
                  onChange={() => handleChange('ticket_resolved_instant_email')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Rendszerben">
                <input type="checkbox" checked={preferences.ticket_resolved_system || false}
                  onChange={() => handleChange('ticket_resolved_system')} />
              </div>
              <div className="notif-mgmt-cell notif-mgmt-checkbox" data-label="Push értesítés">
                <input
                  type="checkbox"
                  checked={preferences.ticket_resolved_push || false}
                  onChange={() => handleChange('ticket_resolved_push')}
                />
              </div>
            </div>
          </div>
        </section>

      <div className="notif-mgmt-actions">
          <button 
            type="submit" 
            className="notif-mgmt-save-btn"
            disabled={saving}
          >
            {saving ? (
              <>
                <FaSpinner className="fa-spin" /> Mentés...
              </>
            ) : (
              'Beállítások mentése'
            )}
          </button>
      </div>
    </form>
    </div>
  );
};

export default NotificationManagement; 